import axios from '../helpers/bearerAxios';
import { logger } from './loggingService';

export type EligibilityStatus = 'success' | 'failed' | 'warning';
export type EligibilityRuleResult = 'pass' | 'fail' | 'warning';
export type EligibilityNoteValue = string | { [key: string]: string | undefined };
export type EligibilityRowNotes = {
    state?: EligibilityNoteValue;
    multiplier?: EligibilityNoteValue;
    propertyType?: EligibilityNoteValue;
    ownershipType?: EligibilityNoteValue;
    installationType?: EligibilityNoteValue;
    systemSpecs?: EligibilityNoteValue;
    tilt?: EligibilityNoteValue;
    systemSize?: EligibilityNoteValue;
    ballasted?: EligibilityNoteValue;
    battery?: EligibilityNoteValue;
    minimumPanel?: EligibilityNoteValue;
    metalShingle?: EligibilityNoteValue;
    offsetMultipleMeters?: EligibilityNoteValue;
    maxProdUsage?: EligibilityNoteValue;
};

export type EligibilityRow = {
    key: string;
    label: string;
    status: EligibilityStatus;
};

export type EligibilityCurrentValues = {
    stateCode: string;
    multiplier: number;
    totalSystemSize: number;
    tiltRange: string;
    tiltMin: number | null;
    tiltMax: number | null;
    tiltRoofs?: Array<{ name: string; tilt: number }>;
    minPanelsPerArray: string;
    smallestArrayCount: number | null;
    moduleCount: number;
    hasBallastedRacking: boolean;
    actualOffsetPercent: string;
    utilityOffsetPercent: string;
    hasBattery: boolean;
    propertyType: string;
    ownershipType: string;
    installationLabel: string;
    installationKeys: string[];
    installationPresence: Record<string, boolean | null>;
    hasMetalShingle: boolean | null;
    offsetsMultipleMeters: boolean | null;
    /** Site likely qualifies as an Energy Community (ITC adder); prefers Enfin. */
    isEnergyCommunity: boolean;
};

export type FinanceEligibilityPartner = {
    id: string;
    displayName: string;
    aliases?: string[];
    paused?: boolean;
    notes?: EligibilityRowNotes;
    state?: {
        contractuallyAvailable?: string[];
        currentlySellable?: string[];
    };
    multiplier?: {
        notRequired?: boolean;
        byState?: Record<string, number>;
    };
    propertyType?: Record<string, EligibilityRuleResult>;
    ownershipType?: Record<string, EligibilityRuleResult>;
    installationType?: Record<string, EligibilityRuleResult>;
    systemSpecs?: {
        tilt?: { min?: number; max?: number };
        systemSize?: {
            minKw?: number;
            defaultMaxKw?: number;
            maxKwByState?: Record<string, number>;
            notKwCapped?: boolean;
        };
        ballasted?: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult };
        battery?: {
            whenPresent?: EligibilityRuleResult;
            whenAbsent?: EligibilityRuleResult;
            failInStates?: string[];
        };
        minimumPanel?: { minCount?: number; scope?: 'system' | 'array' };
        metalShingle?: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult };
        offsetMultipleMeters?: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult };
        maxProdUsage?: {
            noCap?: boolean;
            utilityDiscretion?: boolean;
            passMaxPercent?: number;
            failAbovePercent?: number;
            warningMinPercent?: number;
            warningMaxPercent?: number;
            maxSystemSizeKw?: number;
            byState?: Record<string, Record<string, unknown>>;
        };
    };
};

export type FinanceEligibilityCatalog = {
    version?: number;
    source?: string;
    partners: FinanceEligibilityPartner[];
};

const EMPTY_FINANCE_ELIGIBILITY_CRITERIA: FinanceEligibilityCatalog = { partners: [] };

const PROPERTY_TYPE_ALIASES: Record<string, string> = {
    'single family': 'singleFamily',
    singlefamily: 'singleFamily',
    'single family residential': 'singleFamily',
    singlefamilyresidential: 'singleFamily',
    duplex: 'duplexTriplex',
    triplex: 'duplexTriplex',
    'duplex / triplex': 'duplexTriplex',
    duplextriplex: 'duplexTriplex',
    townhome: 'townhome',
    townhouse: 'townhome',
    condo: 'condo',
    condominium: 'condo',
    'manufactured home': 'manufacturedHome',
    manufacturedhome: 'manufacturedHome',
    'modular home': 'modularHome',
    modularhome: 'modularHome',
    apartment: 'apartments',
    apartments: 'apartments',
    apartments4units: 'apartments',
    'apartments (4+ units)': 'apartments',
    'apartments 4 units': 'apartments',
    rental: 'rentalInvestment',
    investment: 'rentalInvestment',
    'rental / investment': 'rentalInvestment',
    rentalinvestment: 'rentalInvestment',
    'mobile home': 'mobileHome',
    mobilehome: 'mobileHome',
    'vacation home': 'vacationHome',
    vacationhome: 'vacationHome',
    'mixed use': 'mixedUse',
    mixeduse: 'mixedUse',
    mixedusecommresi: 'mixedUse',
    'mixed use comm/resi': 'mixedUse',
    'mixed use (comm/resi)': 'mixedUse',
    farm: 'farmAgRural',
    ag: 'farmAgRural',
    rural: 'farmAgRural',
    'farm / ag / rural': 'farmAgRural',
    farmagrural: 'farmAgRural',
    'golf course': 'golfCourse',
    golfcourse: 'golfCourse',
    pud: 'pud',
};

const OWNERSHIP_TYPE_ALIASES: Record<string, string> = {
    corp: 'corpLlc',
    llc: 'corpLlc',
    'corp / llc': 'corpLlc',
    corpllc: 'corpLlc',
    'exempt property': 'exemptProperty',
    exemptproperty: 'exemptProperty',
    poa: 'poa',
    'power of attorney': 'poa',
    'reverse mortgage': 'reverseMortgage',
    reversemortgage: 'reverseMortgage',
    trust: 'trust',
    trustrevorirrev: 'trust',
    'trust rev or irrev': 'trust',
};

const INSTALLATION_TYPE_ALIASES: Record<string, string> = {
    roofmount: 'roofMount',
    roofmountstandardpitch: 'roofMount',
    flatrooftiltkit: 'flatRoofTiltKit',
    flatroofwtiltkit: 'flatRoofTiltKit',
    metalroof: 'metalRoof',
    metalroofcorrugatedstandingseamonlymetalshinglesexcluded: 'metalRoof',
    groundmount: 'groundMount',
    carport: 'carport',
    pergola: 'pergola',
    detachedstructure: 'detachedStructure',
    overhangporch: 'overhangPorch',
};

/** Criteria for each row will be evaluated against the current partner; warning = not yet known. */
export const ELIGIBILITY_ROWS: EligibilityRow[] = [
    { key: 'energyCommunity', label: 'Energy Community', status: 'warning' },
    { key: 'state', label: 'State Eligibility', status: 'warning' },
    { key: 'multiplier', label: 'Multiplier Eligibility', status: 'warning' },
    { key: 'propertyType', label: 'Property Type Eligibility', status: 'warning' },
    { key: 'ownershipType', label: 'Ownership Type Eligibility', status: 'warning' },
    { key: 'installationType', label: 'Installation Type Eligibility', status: 'warning' },
    { key: 'systemSpecs', label: 'System Specs Eligibility', status: 'warning' },
];

export const SYSTEM_SPEC_ROWS: EligibilityRow[] = [
    { key: 'tilt', label: 'Tilt', status: 'warning' },
    { key: 'systemSize', label: 'System Size', status: 'warning' },
    { key: 'ballasted', label: 'Ballasted Racking', status: 'warning' },
    { key: 'battery', label: 'Battery', status: 'warning' },
    { key: 'minimumPanel', label: 'Smallest Array Size', status: 'warning' },
    { key: 'metalShingle', label: 'Metal Shingle', status: 'warning' },
    { key: 'offsetMultipleMeters', label: 'Offset multiple meters', status: 'warning' },
    { key: 'maxProdUsage', label: 'Max Prod/Usage', status: 'warning' },
];

export const PROPERTY_TYPE_ROWS: EligibilityRow[] = [
    { key: 'singleFamily', label: 'Single Family', status: 'warning' },
    { key: 'duplexTriplex', label: 'Duplex / Triplex', status: 'warning' },
    { key: 'townhome', label: 'Townhome', status: 'warning' },
    { key: 'condo', label: 'Condo', status: 'warning' },
    { key: 'manufacturedHome', label: 'Manufactured Home', status: 'warning' },
    { key: 'modularHome', label: 'Modular Home', status: 'warning' },
    { key: 'apartments', label: 'Apartments (4+ units)', status: 'warning' },
    { key: 'rentalInvestment', label: 'Rental / Investment', status: 'warning' },
    { key: 'mobileHome', label: 'Mobile Home', status: 'warning' },
    { key: 'vacationHome', label: 'Vacation Home', status: 'warning' },
    { key: 'mixedUse', label: 'Mixed Use (Comm/Resi)', status: 'warning' },
    { key: 'farmAgRural', label: 'Farm / Ag / Rural', status: 'warning' },
    { key: 'golfCourse', label: 'Golf Course', status: 'warning' },
    { key: 'pud', label: 'PUD', status: 'warning' },
];

export const INSTALLATION_TYPE_ROWS: EligibilityRow[] = [
    { key: 'roofMount', label: 'Roof Mount', status: 'warning' },
    { key: 'flatRoofTiltKit', label: 'Flat Roof w/ Tilt Kit', status: 'warning' },
    { key: 'metalRoof', label: 'Metal Roof', status: 'warning' },
    { key: 'groundMount', label: 'Ground Mount', status: 'warning' },
    { key: 'carport', label: 'Carport', status: 'warning' },
    { key: 'pergola', label: 'Pergola', status: 'warning' },
    { key: 'detachedStructure', label: 'Detached Structure', status: 'warning' },
    { key: 'overhangPorch', label: 'Overhang Porch', status: 'warning' },
];

export function formatEligibilityCurrentValue(
    key: string,
    values: EligibilityCurrentValues,
): string {
    if (key === 'energyCommunity') {
        return values.isEnergyCommunity ? 'Yes' : 'No';
    }
    if (key === 'state') {
        return values.stateCode || '—';
    }
    if (key === 'multiplier') {
        return Number.isFinite(values.multiplier) ? values.multiplier.toFixed(3) : '—';
    }
    if (key === 'systemSize') {
        return Number.isFinite(values.totalSystemSize)
            ? `${values.totalSystemSize.toFixed(2)} kW`
            : '—';
    }
    if (key === 'tilt') {
        return values.tiltRange || '—';
    }
    if (key === 'minimumPanel') {
        return values.minPanelsPerArray || '—';
    }
    if (key === 'ballasted') {
        return values.hasBallastedRacking ? 'Yes' : 'No';
    }
    if (key === 'maxProdUsage') {
        if (!values.actualOffsetPercent && !values.utilityOffsetPercent) return '—';
        return `${values.actualOffsetPercent || '—'} / ${values.utilityOffsetPercent || '—'}`;
    }
    if (key === 'battery') {
        return values.hasBattery ? 'Yes' : 'No';
    }
    if (key === 'propertyType') {
        return '';
    }
    if (PROPERTY_TYPE_LABELS[key]) {
        const presence = getPropertyTypePresence(values, key);
        if (presence == null) return 'NA';
        return presence ? 'Yes' : 'No';
    }
    if (key === 'ownershipType') {
        return values.ownershipType || '—';
    }
    if (key === 'installationType') {
        return values.installationLabel || '—';
    }
    if (INSTALLATION_TYPE_LABELS[key]) {
        const presence = getInstallationPresence(values, key);
        if (presence == null) return 'NA';
        return presence ? 'Yes' : 'No';
    }
    if (key === 'metalShingle') {
        if (values.hasMetalShingle == null) return '—';
        return values.hasMetalShingle ? 'Yes' : 'No';
    }
    if (key === 'offsetMultipleMeters') {
        if (values.offsetsMultipleMeters == null) return '—';
        return values.offsetsMultipleMeters ? 'Yes' : 'No';
    }
    return '—';
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
    singleFamily: 'Single family',
    duplexTriplex: 'Duplex / triplex',
    townhome: 'Townhome',
    condo: 'Condo',
    manufacturedHome: 'Manufactured home',
    modularHome: 'Modular home',
    apartments: 'Apartments',
    rentalInvestment: 'Rental / investment',
    mobileHome: 'Mobile home',
    vacationHome: 'Vacation home',
    mixedUse: 'Mixed use',
    farmAgRural: 'Farm / ag / rural',
    golfCourse: 'Golf course',
    pud: 'PUD',
};

const OWNERSHIP_TYPE_LABELS: Record<string, string> = {
    corpLlc: 'Corp / LLC',
    exemptProperty: 'Exempt property',
    poa: 'POA',
    reverseMortgage: 'Reverse mortgage',
    trust: 'Trust',
};

const INSTALLATION_TYPE_LABELS: Record<string, string> = {
    roofMount: 'Roof mount',
    flatRoofTiltKit: 'Flat roof tilt kit',
    metalRoof: 'Metal roof',
    groundMount: 'Ground mount',
    carport: 'Carport',
    pergola: 'Pergola',
    detachedStructure: 'Detached structure',
    overhangPorch: 'Overhang / porch',
};

const RULE_RESULT_LABELS: Record<EligibilityRuleResult, string> = {
    pass: 'Allowed',
    fail: 'Not allowed',
    warning: 'Warning / review',
};

export function getEligibilityRowInfo(
    key: string,
    partner: FinanceEligibilityPartner | null,
    values: EligibilityCurrentValues,
    status: EligibilityStatus = 'warning',
): { requirement: string; notes: string; reason: string } {
    if (!partner) {
        return {
            requirement: 'No eligibility criteria for this finance partner.',
            notes: '',
            reason: status === 'success' ? '' : 'No eligibility criteria for this finance partner.',
        };
    }
    const notes = formatEligibilityNotes(key, partner);
    const reason =
        status === 'failed' || status === 'warning'
            ? formatEligibilityReason(key, partner, values, status)
            : '';
    return {
        requirement: formatEligibilityRequirement(key, partner, values),
        notes: reason && notes && reason.includes(notes) ? '' : notes,
        reason,
    };
}

const ROW_NOTE_LABELS: Record<string, Record<string, string>> = {
    propertyType: PROPERTY_TYPE_LABELS,
    ownershipType: OWNERSHIP_TYPE_LABELS,
    installationType: INSTALLATION_TYPE_LABELS,
};

function formatEligibilityNotes(key: string, partner: FinanceEligibilityPartner): string {
    if (PROPERTY_TYPE_LABELS[key]) {
        return formatMappedTypeNote(partner.notes?.propertyType, key);
    }
    if (INSTALLATION_TYPE_LABELS[key]) {
        return formatMappedTypeNote(partner.notes?.installationType, key);
    }
    if (key === 'systemSpecs') {
        const parts = [
            formatNoteValue(partner.notes?.systemSpecs),
            ...SYSTEM_SPEC_ROWS.map((row) => {
                const text = formatNoteValue(partner.notes?.[row.key as keyof EligibilityRowNotes]);
                return text ? `${row.label}: ${text}` : '';
            }),
        ].filter(Boolean);
        return parts.join('\n');
    }
    return formatNoteValue(partner.notes?.[key as keyof EligibilityRowNotes], ROW_NOTE_LABELS[key]);
}

function formatNoteValue(
    raw: EligibilityNoteValue | undefined,
    labels?: Record<string, string>,
): string {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    return Object.entries(raw)
        .map(([noteKey, text]) => {
            const trimmed = String(text ?? '').trim();
            if (!trimmed) return '';
            if (noteKey === 'section' || noteKey === '_all') return trimmed;
            const label = labels?.[noteKey];
            return label ? `${label}: ${trimmed}` : trimmed;
        })
        .filter(Boolean)
        .join('\n');
}

function formatMappedTypeNote(raw: EligibilityNoteValue | undefined, typeKey: string): string {
    if (!raw || typeof raw === 'string') return '';
    return String(raw[typeKey] ?? '').trim();
}

function withNote(reason: string, note: string): string {
    const trimmedNote = note.trim();
    if (!trimmedNote) return reason;
    if (reason.includes(trimmedNote)) return reason;
    return `${reason} ${trimmedNote}`;
}

function formatTiltRoofNames(names: string[]): string {
    const unique = [...new Set(names.map((name) => name.trim()).filter(Boolean))];
    if (unique.length === 0) return '';
    if (unique.length === 1) return unique[0];
    if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
    return `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}`;
}

function formatEligibilityReason(
    key: string,
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    if (key === 'propertyType') {
        const currentKeys = knownPropertyTypeKeys(values);
        if (!currentKeys.length) return 'NA';
        return formatNestedReasons(
            PROPERTY_TYPE_ROWS.filter((row) => currentKeys.includes(row.key)),
            partner,
            values,
            (row) => evaluatePropertyTypeChild(partner, values, row.key),
        );
    }
    if (key === 'installationType') {
        return formatNestedReasons(INSTALLATION_TYPE_ROWS, partner, values, (row) =>
            evaluateInstallationChild(partner, values, row.key),
        );
    }
    if (key === 'systemSpecs') {
        return formatNestedReasons(SYSTEM_SPEC_ROWS, partner, values, (row) =>
            evaluateSystemSpec(partner, values, row.key),
        );
    }
    return formatLeafEligibilityReason(key, partner, values, status);
}

function formatNestedReasons(
    rows: EligibilityRow[],
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    getStatus: (row: EligibilityRow) => EligibilityStatus,
): string {
    const reasons = rows
        .map((row) => {
            const status = getStatus(row);
            if (status !== 'failed' && status !== 'warning') return '';
            const reason = formatLeafEligibilityReason(row.key, partner, values, status);
            return reason ? `${row.label}: ${reason}` : '';
        })
        .filter(Boolean);
    const suffixes = [...new Set(reasons.map((item) => item.replace(/^[^:]+:\s*/, '')))];
    if (suffixes.length === 1 && reasons.length > 1) return suffixes[0];
    return reasons.join('\n');
}

function formatLeafEligibilityReason(
    key: string,
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    if (key === 'energyCommunity') return formatEnergyCommunityReason(partner, values, status);
    if (key === 'state') return formatStateReason(partner, values, status);
    if (key === 'multiplier') return formatMultiplierReason(partner, values, status);
    if (key === 'ownershipType') {
        return formatMappedValueReason(
            'Ownership type',
            values.ownershipType,
            partner.ownershipType,
            OWNERSHIP_TYPE_ALIASES,
            OWNERSHIP_TYPE_LABELS,
            formatEligibilityNotes('ownershipType', partner),
            status,
        );
    }
    if (PROPERTY_TYPE_LABELS[key]) {
        if (!knownPropertyTypeKeys(values).length) return 'NA';
        return formatPresenceMappedReason(
            PROPERTY_TYPE_LABELS[key],
            getPropertyTypePresence(values, key),
            partner.propertyType?.[key],
            formatMappedTypeNote(partner.notes?.propertyType, key),
        );
    }
    if (INSTALLATION_TYPE_LABELS[key]) {
        return formatPresenceMappedReason(
            INSTALLATION_TYPE_LABELS[key],
            getInstallationPresence(values, key),
            partner.installationType?.[key],
            formatMappedTypeNote(partner.notes?.installationType, key),
        );
    }
    if (key === 'tilt') return formatTiltReason(partner, values, status);
    if (key === 'systemSize') return formatSystemSizeReason(partner, values, status);
    if (key === 'ballasted') {
        return formatPresenceValueReason(
            'Ballasted racking',
            values.hasBallastedRacking,
            partner.systemSpecs?.ballasted,
            formatEligibilityNotes('ballasted', partner),
        );
    }
    if (key === 'battery') return formatBatteryReason(partner, values);
    if (key === 'minimumPanel') return formatMinimumPanelReason(partner, values, status);
    if (key === 'metalShingle') {
        return formatPresenceValueReason(
            'Metal shingle',
            values.hasMetalShingle,
            partner.systemSpecs?.metalShingle,
            formatEligibilityNotes('metalShingle', partner),
        );
    }
    if (key === 'offsetMultipleMeters') {
        return formatPresenceValueReason(
            'Offsetting multiple meters',
            values.offsetsMultipleMeters,
            partner.systemSpecs?.offsetMultipleMeters,
            formatEligibilityNotes('offsetMultipleMeters', partner),
        );
    }
    if (key === 'maxProdUsage') return formatMaxProdUsageReason(partner, values, status);
    return status === 'failed' ? 'Does not meet this partner requirement.' : 'Requires review.';
}

function formatPresenceMappedReason(
    label: string,
    presence: boolean | null,
    result: EligibilityRuleResult | undefined,
    note: string,
): string {
    if (presence == null) return 'Current value is not available.';
    if (!presence) return '';
    if (result === 'fail') return withNote(`${label} is not allowed.`, note);
    if (result === 'warning') return withNote(`${label} requires review.`, note);
    if (!result) return `${label} is not mapped for this finance partner.`;
    return '';
}

function formatMappedValueReason(
    label: string,
    current: string,
    rules: Record<string, EligibilityRuleResult> | undefined,
    aliases: Record<string, string>,
    labels: Record<string, string>,
    note: string,
    status: EligibilityStatus,
): string {
    if (!current?.trim()) return `${label} is not available.`;
    const key = normalizeLookupKey(current, aliases);
    if (!key || !rules?.[key]) return `${label} "${current}" is not mapped for this finance partner.`;
    const typeLabel = labels[key] || current;
    const result = rules[key];
    if (result === 'fail' || status === 'failed') return withNote(`${typeLabel} is not allowed.`, note);
    if (result === 'warning' || status === 'warning') return withNote(`${typeLabel} requires review.`, note);
    return '';
}

function formatStateReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const state = values.stateCode.trim().toUpperCase();
    const note = formatEligibilityNotes('state', partner);
    if (!state) return withNote('State is not available.', note);
    if (partner.paused) {
        return withNote('This partner is paused for new solar deals.', note);
    }
    if (status === 'failed') {
        return withNote(`${state} is not currently sellable.`, note);
    }
    return withNote('State eligibility requires review.', note);
}

function formatMultiplierReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const note = formatEligibilityNotes('multiplier', partner);
    if (partner.multiplier?.notRequired) return '';
    if (!Number.isFinite(values.multiplier)) return withNote('Multiplier is not available.', note);
    const state = values.stateCode.trim().toUpperCase();
    const min = partner.multiplier?.byState?.[state];
    if (min == null || !Number.isFinite(min)) {
        return withNote(state ? `No multiplier requirement is defined for ${state}.` : 'State is not available.', note);
    }
    if (status === 'failed') {
        return withNote(
            `Current multiplier ${values.multiplier.toFixed(3)} is below the minimum ${Number(min).toFixed(2)}${state ? ` in ${state}` : ''}.`,
            note,
        );
    }
    return withNote('Multiplier eligibility requires review.', note);
}

function formatTiltReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const note = formatEligibilityNotes('tilt', partner);
    const min = partner.systemSpecs?.tilt?.min;
    const max = partner.systemSpecs?.tilt?.max;
    if (min == null && max == null) return withNote('No tilt requirement is defined.', note);
    if (values.tiltMin == null || values.tiltMax == null) {
        return withNote('Tilt is not available on roofs with panels.', note);
    }
    if (status === 'failed') {
        const ineligible = (values.tiltRoofs ?? []).filter((roof) => {
            if (min != null && roof.tilt < min) return true;
            if (max != null && roof.tilt > max) return true;
            return false;
        });
        const roofList = formatTiltRoofNames(ineligible.map((roof) => roof.name));
        const allowed =
            min != null && max != null
                ? `${min}°–${max}°`
                : min != null
                  ? `minimum ${min}°`
                  : `maximum ${max}°`;
        if (roofList) {
            const below = ineligible.filter((roof) => min != null && roof.tilt < min);
            const above = ineligible.filter((roof) => max != null && roof.tilt > max);
            if (below.length && !above.length) {
                return withNote(`${roofList} ${below.length === 1 ? 'is' : 'are'} below the minimum tilt of ${min}°.`, note);
            }
            if (above.length && !below.length) {
                return withNote(`${roofList} ${above.length === 1 ? 'is' : 'are'} above the maximum tilt of ${max}°.`, note);
            }
            return withNote(`${roofList} ${ineligible.length === 1 ? 'is' : 'are'} outside the allowed tilt of ${allowed}.`, note);
        }
        const range = values.tiltRange || `${values.tiltMin}–${values.tiltMax}`;
        if (min != null && values.tiltMin != null && values.tiltMin < min) {
            return withNote(`Current tilt ${range}° is below the minimum ${min}°.`, note);
        }
        if (max != null && values.tiltMax != null && values.tiltMax > max) {
            return withNote(`Current tilt ${range}° is above the maximum ${max}°.`, note);
        }
        return withNote(`Current tilt ${range}° is outside the allowed range.`, note);
    }
    return withNote('Tilt eligibility requires review.', note);
}

function formatSystemSizeReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const note = formatEligibilityNotes('systemSize', partner);
    const rule = partner.systemSpecs?.systemSize;
    if (!rule) return withNote('No system size requirement is defined.', note);
    if (rule.notKwCapped) return withNote('System size is not kW-capped.', note);
    if (!Number.isFinite(values.totalSystemSize) || values.totalSystemSize <= 0) {
        return withNote('System size is not available.', note);
    }
    const state = values.stateCode.trim().toUpperCase();
    const maxKw = rule.maxKwByState?.[state] ?? rule.defaultMaxKw;
    const current = `${values.totalSystemSize.toFixed(2)} kW`;
    if (rule.minKw != null && values.totalSystemSize < rule.minKw) {
        return withNote(`Current system size ${current} is below the minimum ${rule.minKw} kW.`, note);
    }
    if (maxKw != null && values.totalSystemSize > maxKw) {
        return withNote(
            `Current system size ${current} is above the maximum ${maxKw} kW${state ? ` in ${state}` : ''}.`,
            note,
        );
    }
    if (status === 'warning') return withNote('System size eligibility requires review.', note);
    return '';
}

function formatPresenceValueReason(
    label: string,
    present: boolean | null,
    rule: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult } | undefined,
    note: string,
): string {
    if (!rule) return withNote(`No ${label.toLowerCase()} requirement is defined.`, note);
    if (present == null) return withNote(`${label} is not available.`, note);
    const result = present ? rule.whenPresent : rule.whenAbsent;
    const current = present ? 'present' : 'not present';
    if (result === 'fail') return withNote(`${label} is ${current} and is not allowed.`, note);
    if (result === 'warning') return withNote(`${label} is ${current} and requires review.`, note);
    return '';
}

function formatBatteryReason(partner: FinanceEligibilityPartner, values: EligibilityCurrentValues): string {
    const note = formatEligibilityNotes('battery', partner);
    const rule = partner.systemSpecs?.battery;
    if (!rule) return withNote('No battery requirement is defined.', note);
    const state = values.stateCode.trim().toUpperCase();
    if (values.hasBattery && (rule.failInStates ?? []).map((item) => item.toUpperCase()).includes(state)) {
        return withNote(`Battery is not allowed in ${state || 'this state'}.`, note);
    }
    return formatPresenceValueReason('Battery', values.hasBattery, rule, note);
}

function formatMinimumPanelReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const note = formatEligibilityNotes('minimumPanel', partner);
    const rule = partner.systemSpecs?.minimumPanel;
    if (!rule?.minCount) return withNote('No minimum panel requirement is defined.', note);
    const count = rule.scope === 'system' ? values.moduleCount : values.smallestArrayCount;
    const scope = rule.scope === 'system' ? 'system-wide' : 'in the smallest array';
    if (count == null || !Number.isFinite(count) || count <= 0) {
        return withNote('Panel count is not available.', note);
    }
    if (status === 'failed') {
        return withNote(`${count} panels ${scope} is below the minimum of ${rule.minCount}.`, note);
    }
    return withNote('Minimum panel eligibility requires review.', note);
}

function formatMaxProdUsageReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    const note = formatEligibilityNotes('maxProdUsage', partner);
    const rule = partner.systemSpecs?.maxProdUsage;
    if (!rule) return withNote('No max production/usage requirement is defined.', note);
    const state = values.stateCode.trim().toUpperCase();
    const stateRule = (rule.byState?.[state] ?? {}) as {
        noCap?: boolean;
        utilityDiscretion?: boolean;
        passMaxPercent?: number;
        failAbovePercent?: number;
        warningMinPercent?: number;
        warningMaxPercent?: number;
        maxSystemSizeKw?: number;
    };
    const effective = { ...rule, ...stateRule };
    if (effective.maxSystemSizeKw != null) {
        if (!Number.isFinite(values.totalSystemSize) || values.totalSystemSize <= 0) {
            return withNote('System size is not available for max production/usage.', note);
        }
        if (status === 'failed') {
            return withNote(
                `Current system size ${values.totalSystemSize.toFixed(2)} kW exceeds the ${effective.maxSystemSizeKw} kW cap${state ? ` in ${state}` : ''}.`,
                note,
            );
        }
    }
    const offset =
        parsePercent(values.utilityOffsetPercent) ?? parsePercent(values.actualOffsetPercent);
    if (effective.utilityDiscretion && offset == null) {
        return withNote('Max production/usage is subject to utility discretion.', note);
    }
    if (offset == null) return withNote('Production/usage offset is not available.', note);
    if (effective.failAbovePercent != null && offset > effective.failAbovePercent) {
        return withNote(`Current offset ${offset}% is above the maximum ${effective.failAbovePercent}%.`, note);
    }
    if (
        effective.warningMinPercent != null &&
        effective.warningMaxPercent != null &&
        offset >= effective.warningMinPercent &&
        offset <= effective.warningMaxPercent
    ) {
        return withNote(
            `Current offset ${offset}% is in the review range ${effective.warningMinPercent}–${effective.warningMaxPercent}%.`,
            note,
        );
    }
    if (effective.utilityDiscretion) return withNote('Max production/usage is subject to utility discretion.', note);
    if (status === 'failed') return withNote(`Current offset ${offset}% does not meet the partner cap.`, note);
    return withNote('Max production/usage requires review.', note);
}

function evaluateSystemSpec(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    key: string,
): EligibilityStatus {
    if (key === 'tilt') return evaluateTilt(partner, values);
    if (key === 'systemSize') return evaluateSystemSize(partner, values);
    if (key === 'ballasted') return evaluatePresenceRule(partner.systemSpecs?.ballasted, values.hasBallastedRacking);
    if (key === 'battery') return evaluateBattery(partner, values);
    if (key === 'minimumPanel') return evaluateMinimumPanel(partner, values);
    if (key === 'metalShingle') return evaluatePresenceRule(partner.systemSpecs?.metalShingle, values.hasMetalShingle);
    if (key === 'offsetMultipleMeters') {
        return evaluatePresenceRule(partner.systemSpecs?.offsetMultipleMeters, values.offsetsMultipleMeters);
    }
    if (key === 'maxProdUsage') return evaluateMaxProdUsage(partner, values);
    return 'warning';
}

export function formatEligibilityRequirement(
    key: string,
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): string {
    if (key === 'energyCommunity') return formatEnergyCommunityRequirement(partner);
    if (key === 'state') return formatStateRequirement(partner);
    if (key === 'multiplier') return formatMultiplierRequirement(partner, values);
    if (key === 'propertyType') {
        return formatMappedRequirement(
            partner.propertyType,
            PROPERTY_TYPE_LABELS,
            values.propertyType,
            PROPERTY_TYPE_ALIASES,
            knownPropertyTypeKeys(values),
        );
    }
    if (PROPERTY_TYPE_LABELS[key]) {
        return formatRuleResult(partner.propertyType?.[key]);
    }
    if (key === 'ownershipType') {
        return formatMappedRequirement(
            partner.ownershipType,
            OWNERSHIP_TYPE_LABELS,
            values.ownershipType,
            OWNERSHIP_TYPE_ALIASES,
        );
    }
    if (key === 'installationType') {
        return formatMappedRequirement(
            partner.installationType,
            INSTALLATION_TYPE_LABELS,
            values.installationLabel,
            INSTALLATION_TYPE_ALIASES,
            values.installationKeys,
        );
    }
    if (INSTALLATION_TYPE_LABELS[key]) {
        return formatRuleResult(partner.installationType?.[key]);
    }
    if (key === 'systemSpecs') {
        return SYSTEM_SPEC_ROWS.map(
            (row) => `${row.label}: ${formatEligibilityRequirement(row.key, partner, values)}`,
        ).join(' ');
    }
    if (key === 'tilt') return formatTiltRequirement(partner);
    if (key === 'systemSize') return formatSystemSizeRequirement(partner, values);
    if (key === 'ballasted') return formatPresenceRequirement(partner.systemSpecs?.ballasted);
    if (key === 'battery') return formatBatteryRequirement(partner);
    if (key === 'minimumPanel') return formatMinimumPanelRequirement(partner);
    if (key === 'metalShingle') return formatPresenceRequirement(partner.systemSpecs?.metalShingle);
    if (key === 'offsetMultipleMeters') return formatPresenceRequirement(partner.systemSpecs?.offsetMultipleMeters);
    if (key === 'maxProdUsage') return formatMaxProdUsageRequirement(partner, values);
    return 'No requirement defined.';
}

function formatRuleResult(result: EligibilityRuleResult | undefined): string {
    return result ? RULE_RESULT_LABELS[result] : 'Not defined';
}

function formatList(items: string[] | undefined): string {
    return (items ?? []).filter(Boolean).join(', ') || 'None';
}

function formatEnergyCommunityRequirement(partner: FinanceEligibilityPartner): string {
    if (isEnergyCommunityPreferredPartner(partner)) {
        return 'Preferred when the site likely qualifies as an Energy Community.';
    }
    return 'Energy Community sites prefer Enfin. This partner can still be used after review.';
}

function formatEnergyCommunityReason(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    status: EligibilityStatus,
): string {
    if (!values.isEnergyCommunity) return '';
    if (status === 'warning' && !isEnergyCommunityPreferredPartner(partner)) {
        return 'This address likely qualifies as an Energy Community. Enfin is the preferred finance partner.';
    }
    return '';
}

function formatStateRequirement(partner: FinanceEligibilityPartner): string {
    const parts: string[] = [];
    if (partner.paused) parts.push('This partner is paused.');
    parts.push(`Currently sellable: ${formatList(partner.state?.currentlySellable)}.`);
    parts.push(`Contractually available: ${formatList(partner.state?.contractuallyAvailable)}.`);
    return parts.join(' ');
}

function formatMultiplierRequirement(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): string {
    if (partner.multiplier?.notRequired) return 'Not required.';
    const byState = partner.multiplier?.byState ?? {};
    const state = values.stateCode.trim().toUpperCase();
    const parts: string[] = [];
    if (state && byState[state] != null) {
        parts.push(`Minimum ${Number(byState[state]).toFixed(2)} in ${state}.`);
    }
    const entries = Object.entries(byState);
    if (entries.length) {
        parts.push(`By state: ${entries.map(([code, min]) => `${code} ${Number(min).toFixed(2)}`).join(', ')}.`);
    }
    return parts.join(' ') || 'No requirement defined.';
}

function formatMappedRequirement(
    rules: Record<string, EligibilityRuleResult> | undefined,
    labels: Record<string, string>,
    current: string,
    aliases: Record<string, string>,
    currentKeys?: string[],
): string {
    if (!rules) return 'No requirement defined.';
    const groups: Record<EligibilityRuleResult, string[]> = { pass: [], warning: [], fail: [] };
    for (const [ruleKey, result] of Object.entries(rules)) {
        groups[result]?.push(labels[ruleKey] || ruleKey);
    }
    const parts: string[] = [];
    const resolvedKeys =
        currentKeys?.filter(Boolean) ??
        (current ? [normalizeLookupKey(current, aliases)].filter(Boolean) : []);
    if (resolvedKeys.length) {
        const currentResults = resolvedKeys.map((ruleKey) => {
            const label = labels[ruleKey] || current || ruleKey;
            return `${label}: ${formatRuleResult(rules[ruleKey])}`;
        });
        parts.push(`Current — ${currentResults.join('; ')}.`);
    } else if (current) {
        parts.push(`Current (${current}): not mapped.`);
    }
    if (groups.pass.length) parts.push(`Allowed: ${groups.pass.join(', ')}.`);
    if (groups.warning.length) parts.push(`Warning / review: ${groups.warning.join(', ')}.`);
    if (groups.fail.length) parts.push(`Not allowed: ${groups.fail.join(', ')}.`);
    return parts.join(' ') || 'No requirement defined.';
}

function formatTiltRequirement(partner: FinanceEligibilityPartner): string {
    const min = partner.systemSpecs?.tilt?.min;
    const max = partner.systemSpecs?.tilt?.max;
    if (min == null && max == null) return 'No tilt requirement defined.';
    if (min != null && max != null) return `${min}°–${max}° on roofs with panels.`;
    if (min != null) return `Minimum ${min}° on roofs with panels.`;
    return `Maximum ${max}° on roofs with panels.`;
}

function formatSystemSizeRequirement(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): string {
    const rule = partner.systemSpecs?.systemSize;
    if (!rule) return 'No requirement defined.';
    if (rule.notKwCapped) return 'Not kW-capped.';
    const parts: string[] = [];
    const state = values.stateCode.trim().toUpperCase();
    const maxKw = rule.maxKwByState?.[state] ?? rule.defaultMaxKw;
    if (rule.minKw != null && maxKw != null) {
        parts.push(`${rule.minKw}–${maxKw} kW${state ? ` in ${state}` : ''}.`);
    } else if (rule.minKw != null) {
        parts.push(`Minimum ${rule.minKw} kW.`);
    } else if (maxKw != null) {
        parts.push(`Maximum ${maxKw} kW${state ? ` in ${state}` : ''}.`);
    }
    const byState = Object.entries(rule.maxKwByState ?? {});
    if (byState.length) {
        parts.push(`By state: ${byState.map(([code, kw]) => `${code} ${kw} kW`).join(', ')}.`);
    } else if (rule.defaultMaxKw != null) {
        parts.push(`Default maximum ${rule.defaultMaxKw} kW.`);
    }
    return parts.join(' ') || 'No requirement defined.';
}

function formatPresenceRequirement(
    rule: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult } | undefined,
): string {
    if (!rule) return 'No requirement defined.';
    return `When present: ${formatRuleResult(rule.whenPresent)}. When absent: ${formatRuleResult(rule.whenAbsent)}.`;
}

function formatBatteryRequirement(partner: FinanceEligibilityPartner): string {
    const rule = partner.systemSpecs?.battery;
    if (!rule) return 'No requirement defined.';
    const failStates = (rule.failInStates ?? []).filter(Boolean);
    const extra = failStates.length ? ` Not allowed in ${failStates.join(', ')}.` : '';
    return `${formatPresenceRequirement(rule)}${extra}`;
}

function formatMinimumPanelRequirement(partner: FinanceEligibilityPartner): string {
    const rule = partner.systemSpecs?.minimumPanel;
    if (!rule?.minCount) return 'No requirement defined.';
    if (rule.scope === 'system') return `Minimum ${rule.minCount} panels system-wide.`;
    return `Minimum ${rule.minCount} panel${rule.minCount === 1 ? '' : 's'} per array.`;
}

function formatMaxProdUsageRequirement(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): string {
    const rule = partner.systemSpecs?.maxProdUsage;
    if (!rule) return 'No requirement defined.';
    const state = values.stateCode.trim().toUpperCase();
    const stateRule = (rule.byState?.[state] ?? {}) as Record<string, unknown>;
    const effective = { ...rule, ...stateRule };
    const parts: string[] = [];
    if (effective.noCap) parts.push('No production/usage cap.');
    if (effective.utilityDiscretion) parts.push('Subject to utility discretion.');
    if (effective.maxSystemSizeKw != null) {
        parts.push(`Maximum system size ${effective.maxSystemSizeKw} kW.`);
    }
    if (effective.passMaxPercent != null) {
        parts.push(`Pass at or below ${effective.passMaxPercent}%.`);
    }
    if (effective.warningMinPercent != null && effective.warningMaxPercent != null) {
        parts.push(`Warning ${effective.warningMinPercent}–${effective.warningMaxPercent}%.`);
    }
    if (effective.failAbovePercent != null) {
        parts.push(`Fail above ${effective.failAbovePercent}%.`);
    }
    const exceptions = Object.entries(rule.byState ?? {});
    if (exceptions.length) {
        parts.push(
            `By state: ${exceptions
                .map(([code, raw]) => `${code} ${formatMaxProdUsageStateRule(raw as Record<string, unknown>)}`)
                .join('; ')}.`,
        );
    }
    return parts.join(' ') || 'No requirement defined.';
}

function formatMaxProdUsageStateRule(rule: Record<string, unknown>): string {
    const bits: string[] = [];
    if (rule.noCap) bits.push('no cap');
    if (rule.utilityDiscretion) bits.push('utility discretion');
    if (rule.maxSystemSizeKw != null) bits.push(`max ${rule.maxSystemSizeKw} kW`);
    if (rule.passMaxPercent != null) bits.push(`pass ≤ ${rule.passMaxPercent}%`);
    if (rule.warningMinPercent != null && rule.warningMaxPercent != null) {
        bits.push(`warning ${rule.warningMinPercent}–${rule.warningMaxPercent}%`);
    }
    if (rule.failAbovePercent != null) bits.push(`fail > ${rule.failAbovePercent}%`);
    return bits.join(', ') || 'see partner notes';
}

function isEligibilityRuleResult(value: unknown): value is EligibilityRuleResult {
    return value === 'pass' || value === 'fail' || value === 'warning';
}

function toFiniteNumber(value: unknown): number | undefined {
    if (value == null || value === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
}

function pickRaw<T = unknown>(source: Record<string, unknown> | null | undefined, ...keys: string[]): T | undefined {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined) return source[key] as T;
    }
    return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function asStringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function remapKeyedRules(
    raw: unknown,
    aliases: Record<string, string>,
    identityKeys: string[],
): Record<string, EligibilityRuleResult> | undefined {
    const record = asRecord(raw);
    if (!record) return undefined;
    const identityByCompact = Object.fromEntries(identityKeys.map((key) => [key.toLowerCase(), key]));
    const mapped: Record<string, EligibilityRuleResult> = {};
    for (const [rawKey, value] of Object.entries(record)) {
        if (!isEligibilityRuleResult(value)) continue;
        const compact = rawKey.toLowerCase().replace(/[\s/_-]+/g, '');
        const key = aliases[compact] || identityByCompact[compact];
        if (key) mapped[key] = value;
    }
    return mapped;
}

function noteText(value: unknown): string | undefined {
    const text = String(value ?? '').trim();
    return text || undefined;
}

function addNote(notes: EligibilityRowNotes, key: keyof EligibilityRowNotes, value: unknown): void {
    const text = noteText(value);
    if (text) notes[key] = text;
}

function parseFailInStates(notes: string | undefined, existing?: unknown): string[] | undefined {
    if (Array.isArray(existing) && existing.length) {
        return existing.map((item) => String(item).toUpperCase()).filter(Boolean);
    }
    if (!notes) return undefined;
    const match = notes.match(/not currently sold in\s+([A-Z]{2}(?:\s*(?:,|and)\s*[A-Z]{2})*)/i);
    if (!match) return undefined;
    return (match[1].match(/[A-Z]{2}/gi) ?? []).map((code) => code.toUpperCase());
}

function mapPresenceRule(raw: unknown): { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult } | undefined {
    const record = asRecord(raw);
    if (!record) return undefined;
    const whenPresent = isEligibilityRuleResult(record.whenPresent)
        ? record.whenPresent
        : isEligibilityRuleResult(record.code)
          ? record.code
          : undefined;
    if (!whenPresent && !isEligibilityRuleResult(record.whenAbsent)) return undefined;
    return {
        whenPresent,
        whenAbsent: isEligibilityRuleResult(record.whenAbsent) ? record.whenAbsent : 'pass',
    };
}

function mapSystemSizeRule(raw: unknown): NonNullable<FinanceEligibilityPartner['systemSpecs']>['systemSize'] | undefined {
    const record = asRecord(raw);
    if (!record) return undefined;
    const minKw = toFiniteNumber(record.min ?? record.minKw);
    const defaultMaxKw = toFiniteNumber(record.max ?? record.defaultMaxKw);
    const maxKwByState = asRecord(record.maxKwByState) as Record<string, number> | undefined;
    const notKwCapped =
        Boolean(record.notKwCapped) || (record.code != null && minKw == null && defaultMaxKw == null && !maxKwByState);
    if (notKwCapped) return { notKwCapped: true };
    if (minKw == null && defaultMaxKw == null && !maxKwByState) return undefined;
    return { minKw, defaultMaxKw, maxKwByState };
}

function mapMinimumPanelRule(
    raw: unknown,
): NonNullable<FinanceEligibilityPartner['systemSpecs']>['minimumPanel'] | undefined {
    const record = asRecord(raw);
    if (!record) return undefined;
    const minCount = toFiniteNumber(record.min ?? record.minCount);
    if (minCount == null) return undefined;
    const notes = noteText(record.notes);
    const scope: 'system' | 'array' =
        record.scope === 'system' || /system[- ]wide/i.test(notes ?? '') ? 'system' : 'array';
    return { minCount, scope };
}

function mapSystemSpecs(
    raw: unknown,
    notes: EligibilityRowNotes,
): FinanceEligibilityPartner['systemSpecs'] | undefined {
    const record = asRecord(raw);
    if (!record) return undefined;

    const tiltRaw = asRecord(pickRaw(record, 'tiltRange', 'tilt'));
    const tiltMin = toFiniteNumber(tiltRaw?.min);
    const tiltMax = toFiniteNumber(tiltRaw?.max);
    const tilt = tiltMin != null || tiltMax != null ? { min: tiltMin, max: tiltMax } : undefined;
    addNote(notes, 'tilt', tiltRaw?.notes);

    const systemSizeRaw = pickRaw(record, 'minMaxSystemSizeKwDc', 'systemSize');
    const systemSize = mapSystemSizeRule(systemSizeRaw);
    addNote(notes, 'systemSize', asRecord(systemSizeRaw)?.notes);

    const ballastedRaw = pickRaw(record, 'ballastedMount', 'ballasted');
    const ballasted = mapPresenceRule(ballastedRaw);
    addNote(notes, 'ballasted', asRecord(ballastedRaw)?.notes);

    const batteryRaw = pickRaw(record, 'batteryAddOn', 'battery');
    const batteryRecord = asRecord(batteryRaw);
    const batteryPresence = mapPresenceRule(batteryRaw);
    const batteryNotes = noteText(batteryRecord?.notes);
    addNote(notes, 'battery', batteryNotes);
    const battery = batteryPresence
        ? { ...batteryPresence, failInStates: parseFailInStates(batteryNotes, batteryRecord?.failInStates) }
        : undefined;

    const panelRaw = pickRaw(record, 'minPanelsPerArraySystem', 'minimumPanel');
    const minimumPanel = mapMinimumPanelRule(panelRaw);
    addNote(notes, 'minimumPanel', asRecord(panelRaw)?.notes);

    const metalRaw = pickRaw(record, 'metalShingles', 'metalShingle');
    const metalShingle = mapPresenceRule(metalRaw);
    addNote(notes, 'metalShingle', asRecord(metalRaw)?.notes);

    const offsetRaw = pickRaw(record, 'offsetMultipleMeters');
    const offsetMultipleMeters = mapPresenceRule(offsetRaw);
    addNote(notes, 'offsetMultipleMeters', asRecord(offsetRaw)?.notes);

    const maxProdUsage = asRecord(pickRaw(record, 'maxProdUsage')) as
        | NonNullable<FinanceEligibilityPartner['systemSpecs']>['maxProdUsage']
        | undefined;
    addNote(notes, 'maxProdUsage', asRecord(maxProdUsage)?.notes);
    addNote(notes, 'systemSpecs', record.notes);

    if (
        !tilt &&
        !systemSize &&
        !ballasted &&
        !battery &&
        !minimumPanel &&
        !metalShingle &&
        !offsetMultipleMeters &&
        !maxProdUsage
    ) {
        return undefined;
    }

    return {
        tilt,
        systemSize,
        ballasted,
        battery,
        minimumPanel,
        metalShingle,
        offsetMultipleMeters,
        maxProdUsage,
    };
}

function normalizeFinanceEligibilityPartner(raw: unknown): FinanceEligibilityPartner | null {
    const record = asRecord(raw);
    if (!record) return null;
    const id = String(pickRaw(record, 'Id', 'id') ?? '')
        .trim()
        .toLowerCase();
    if (!id) return null;

    const notes: EligibilityRowNotes = { ...asRecord(pickRaw(record, 'notes')) } as EligibilityRowNotes;
    const stateRaw = asRecord(pickRaw(record, 'State', 'state'));
    const multiplierRaw = asRecord(pickRaw(record, 'Multiplier', 'multiplier'));
    const byState = asRecord(pickRaw(multiplierRaw, 'ByState', 'byState')) as Record<string, number> | undefined;

    return {
        id,
        displayName: String(pickRaw(record, 'DisplayName', 'displayName') ?? id),
        aliases: asStringArray(pickRaw(record, 'Aliases', 'aliases')),
        paused: Boolean(pickRaw(record, 'Paused', 'paused')),
        notes,
        state: {
            contractuallyAvailable: asStringArray(pickRaw(stateRaw, 'ContractuallyAvailable', 'contractuallyAvailable')),
            currentlySellable: asStringArray(pickRaw(stateRaw, 'CurrentlySellable', 'currentlySellable')),
        },
        multiplier: {
            byState,
            notRequired: Boolean(pickRaw(multiplierRaw, 'NotRequired', 'notRequired')),
        },
        propertyType: remapKeyedRules(
            pickRaw(record, 'PropertyType', 'propertyType'),
            PROPERTY_TYPE_ALIASES,
            PROPERTY_TYPE_ROWS.map((row) => row.key),
        ),
        ownershipType: remapKeyedRules(
            pickRaw(record, 'OwnershipType', 'ownershipType'),
            OWNERSHIP_TYPE_ALIASES,
            Object.keys(OWNERSHIP_TYPE_LABELS),
        ),
        installationType: remapKeyedRules(
            pickRaw(record, 'InstallationType', 'installationType'),
            INSTALLATION_TYPE_ALIASES,
            INSTALLATION_TYPE_ROWS.map((row) => row.key),
        ),
        systemSpecs: mapSystemSpecs(pickRaw(record, 'SystemSpecs', 'systemSpecs'), notes),
    };
}

function normalizeFinanceEligibilityCatalog(raw: unknown): FinanceEligibilityCatalog {
    const record = asRecord(raw);
    const nested = asRecord(pickRaw(record, 'data', 'Data')) ?? record;
    const partners = (pickRaw<unknown[]>(nested, 'Partners', 'partners') ?? [])
        .map(normalizeFinanceEligibilityPartner)
        .filter((partner): partner is FinanceEligibilityPartner => partner != null);
    return {
        version: toFiniteNumber(pickRaw(nested, 'Version', 'version')),
        source: noteText(pickRaw(nested, 'Source', 'source')),
        partners,
    };
}

export async function fetchFinanceEligibilityCriteria(financePartner?: string | null): Promise<FinanceEligibilityCatalog> {
    try {
        const path = `${import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_BASE_URL}${import.meta.env.VITE_FETCH_FINANCE_ELIGIBILITY_CRITERIA}`;
        const partner = String(financePartner ?? '').trim();
        const url = partner ? `${path}?financePartner=${encodeURIComponent(partner)}` : path;
        const response = await axios.get(url, { timeout: 4000 });
        const catalog = normalizeFinanceEligibilityCatalog(response?.data);
        if (catalog.partners.length > 0) {
            return catalog;
        }
        logger.warn('Finance eligibility API returned no partners');
    } catch (error) {
        logger.warn({ error }, 'Finance eligibility API unavailable');
    }
    return EMPTY_FINANCE_ELIGIBILITY_CRITERIA;
}

export type FinanceEligibilityPropertyDetails = {
    propertyType: string;
    ownershipType: string;
};

const EMPTY_FINANCE_ELIGIBILITY_PROPERTY_DETAILS: FinanceEligibilityPropertyDetails = {
    propertyType: '',
    ownershipType: '',
};

function optionalEligibilityText(value: unknown): string {
    if (Array.isArray(value)) {
        return value.map(optionalEligibilityText).filter(Boolean).join(', ');
    }
    if (value == null || typeof value === 'boolean') return '';
    const text = String(value).trim();
    if (!text || /^(null|undefined)$/i.test(text)) return '';
    return text;
}

function normalizeFinanceEligibilityPropertyDetails(raw: unknown): FinanceEligibilityPropertyDetails {
    const record = asRecord(raw);
    const nested = asRecord(pickRaw(record, 'data', 'Data')) ?? record;
    return {
        propertyType: optionalEligibilityText(pickRaw(nested, 'propertyType', 'PropertyType')),
        ownershipType: optionalEligibilityText(pickRaw(nested, 'ownershipType', 'OwnershipType')),
    };
}

function resolveOpportunitySalesForceId(salesForceId?: string | null): string {
    const explicit = String(salesForceId ?? '').trim();
    if (explicit) return explicit;
    if (typeof sessionStorage === 'undefined') return '';
    return sessionStorage.getItem('SalesForceId')?.trim() ?? '';
}

export async function fetchFinanceEligibilityPropertyDetails(
    salesForceId?: string | null,
): Promise<FinanceEligibilityPropertyDetails> {
    const sfid = resolveOpportunitySalesForceId(salesForceId);
    const base = String(import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_BASE_URL ?? '').replace(/\/$/, '');
    const template = String(import.meta.env.VITE_FETCH_OPPORUNITY_PROPERTY_DETAILS ?? '').trim();
    if (!sfid || !base || !template) return EMPTY_FINANCE_ELIGIBILITY_PROPERTY_DETAILS;
    try {
        const path = template.includes('{sfid}')
            ? template.replace('{sfid}', encodeURIComponent(sfid))
            : `${template.replace(/\/$/, '')}/${encodeURIComponent(sfid)}`;
        const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
        const response = await axios.get(url, { timeout: 4000 });
        return normalizeFinanceEligibilityPropertyDetails(response?.data);
    } catch (error) {
        logger.warn({ error }, 'Finance eligibility property details API unavailable');
        return EMPTY_FINANCE_ELIGIBILITY_PROPERTY_DETAILS;
    }
}

const FINANCE_ELIGIBILITY_VALUE_PARTNER_IDS = new Set(['igs', 'enfin', 'lightreach', 'goodleap']);

export function isFinanceEligibilityValuePartner(
    partner: Pick<FinanceEligibilityPartner, 'id'> | string | null | undefined,
): boolean {
    const id =
        typeof partner === 'string'
            ? partner.trim().toLowerCase()
            : String(partner?.id ?? '').trim().toLowerCase();
    return FINANCE_ELIGIBILITY_VALUE_PARTNER_IDS.has(id);
}

function unusedEligibilityStatuses(): Record<string, EligibilityStatus> {
    return Object.fromEntries(
        [...ELIGIBILITY_ROWS, ...SYSTEM_SPEC_ROWS, ...PROPERTY_TYPE_ROWS, ...INSTALLATION_TYPE_ROWS].map((row) => [
            row.key,
            'warning' as EligibilityStatus,
        ]),
    );
}

export function findFinanceEligibilityPartner(
    partnerName: string | null | undefined,
    catalog: FinanceEligibilityCatalog | null | undefined,
): FinanceEligibilityPartner | null {
    const name = String(partnerName ?? '').trim().toLowerCase();
    if (!name) return null;
    return (
        (catalog?.partners ?? []).find((partner) => {
            if (partner.id.toLowerCase() === name) return true;
            if (partner.displayName.toLowerCase() === name) return true;
            return (partner.aliases ?? []).some((alias) => alias.toLowerCase() === name);
        }) ?? null
    );
}

export function evaluateFinanceEligibility(
    partnerName: string,
    values: EligibilityCurrentValues,
    catalog: FinanceEligibilityCatalog | null | undefined,
): Record<string, EligibilityStatus> {
    const partner = findFinanceEligibilityPartner(partnerName, catalog);
    if (!partner || !isFinanceEligibilityValuePartner(partner)) {
        return unusedEligibilityStatuses();
    }

    const specStatuses: Record<string, EligibilityStatus> = {
        tilt: evaluateTilt(partner, values),
        systemSize: evaluateSystemSize(partner, values),
        ballasted: evaluatePresenceRule(partner.systemSpecs?.ballasted, values.hasBallastedRacking),
        battery: evaluateBattery(partner, values),
        minimumPanel: evaluateMinimumPanel(partner, values),
        metalShingle: evaluatePresenceRule(partner.systemSpecs?.metalShingle, values.hasMetalShingle),
        offsetMultipleMeters: evaluatePresenceRule(
            partner.systemSpecs?.offsetMultipleMeters,
            values.offsetsMultipleMeters,
        ),
        maxProdUsage: evaluateMaxProdUsage(partner, values),
    };

    const propertyTypeStatuses = Object.fromEntries(
        PROPERTY_TYPE_ROWS.map((row) => [row.key, evaluatePropertyTypeChild(partner, values, row.key)]),
    );
    const installationTypeStatuses = Object.fromEntries(
        INSTALLATION_TYPE_ROWS.map((row) => [row.key, evaluateInstallationChild(partner, values, row.key)]),
    );

    return {
        energyCommunity: evaluateEnergyCommunity(partner, values),
        state: evaluateState(partner, values),
        multiplier: evaluateMultiplier(partner, values),
        propertyType: evaluatePropertyType(partner, values),
        ownershipType: evaluateMappedRule(partner.ownershipType, values.ownershipType, OWNERSHIP_TYPE_ALIASES),
        installationType: evaluateInstallation(partner, values),
        systemSpecs: worstStatus(Object.values(specStatuses)),
        ...specStatuses,
        ...propertyTypeStatuses,
        ...installationTypeStatuses,
    };
}

export type EligibilityReasonRow = {
    key: string;
    label: string;
    group?: string;
    status: EligibilityStatus;
    reason: string;
};

export function getFailedSystemSpecReasons(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    statusByKey: Record<string, EligibilityStatus>,
): EligibilityReasonRow[] {
    return SYSTEM_SPEC_ROWS.flatMap((row) => {
        if (statusByKey[row.key] !== 'failed') return [];
        const reason = getEligibilityRowInfo(row.key, partner, values, 'failed').reason;
        return [
            {
                key: row.key,
                label: row.label,
                group: 'System Specs',
                status: 'failed' as const,
                reason: reason || `${row.label} is not eligible for this finance partner.`,
            },
        ];
    });
}

export function getOverallEligibilityStatus(statusByKey: Record<string, EligibilityStatus>): EligibilityStatus {
    return worstStatus(ELIGIBILITY_ROWS.map((row) => statusByKey[row.key] ?? 'warning'));
}

function nestedRowsForParent(parentKey: string): EligibilityRow[] | null {
    if (parentKey === 'propertyType') return PROPERTY_TYPE_ROWS;
    if (parentKey === 'installationType') return INSTALLATION_TYPE_ROWS;
    if (parentKey === 'systemSpecs') return SYSTEM_SPEC_ROWS;
    return null;
}

export function getOverallEligibilityReasonRows(
    partner: FinanceEligibilityPartner | null,
    values: EligibilityCurrentValues,
    statusByKey: Record<string, EligibilityStatus>,
): EligibilityReasonRow[] {
    if (!partner) {
        return [
            {
                key: 'overall',
                label: 'Eligibility',
                status: 'warning',
                reason: 'No eligibility criteria for this finance partner.',
            },
        ];
    }
    const rows: EligibilityReasonRow[] = [];
    for (const row of ELIGIBILITY_ROWS) {
        const status = statusByKey[row.key] ?? 'warning';
        if (status !== 'failed' && status !== 'warning') continue;
        const parentLabel = row.label.replace(/ Eligibility$/, '');
        const nested = nestedRowsForParent(row.key);
        if (nested) {
            if (row.key === 'propertyType' && !knownPropertyTypeKeys(values).length) {
                rows.push({
                    key: row.key,
                    group: parentLabel,
                    label: '',
                    status,
                    reason: 'NA',
                });
                continue;
            }
            const childRows = nested.flatMap((child) => {
                if (row.key === 'propertyType' && !isCurrentPropertyType(child.key, values)) return [];
                const childStatus = statusByKey[child.key] ?? 'warning';
                if (childStatus !== 'failed' && childStatus !== 'warning') return [];
                const reason = getEligibilityRowInfo(child.key, partner, values, childStatus).reason;
                if (!reason) return [];
                return [
                    {
                        key: `${row.key}-${child.key}`,
                        group: parentLabel,
                        label: child.label,
                        status: childStatus,
                        reason,
                    },
                ];
            });
            if (childRows.length) {
                rows.push(...childRows);
                continue;
            }
        }
        const reason = getEligibilityRowInfo(row.key, partner, values, status).reason;
        if (reason) {
            rows.push({ key: row.key, group: parentLabel, label: '', status, reason });
        }
    }
    return rows;
}

export function formatOverallEligibilityReason(
    partner: FinanceEligibilityPartner | null,
    values: EligibilityCurrentValues,
    statusByKey: Record<string, EligibilityStatus>,
): string {
    return getOverallEligibilityReasonRows(partner, values, statusByKey)
        .map((row) => {
            const title = row.group || row.label;
            return row.label && row.group ? `${title}: ${row.label}: ${row.reason}` : `${title}: ${row.reason}`;
        })
        .join('\n');
}

function toStatus(result: EligibilityRuleResult | undefined): EligibilityStatus {
    if (result === 'pass') return 'success';
    if (result === 'fail') return 'failed';
    return 'warning';
}

function worstStatus(statuses: EligibilityStatus[]): EligibilityStatus {
    if (statuses.includes('failed')) return 'failed';
    if (statuses.includes('warning') || statuses.length === 0) return 'warning';
    return 'success';
}

function normalizeLookupKey(value: string, aliases: Record<string, string>): string {
    const compact = value.toLowerCase().replace(/[\s/_-]+/g, ' ').trim();
    if (!compact) return '';
    return aliases[compact] || aliases[compact.replace(/\s+/g, '')] || '';
}

export function isCurrentPropertyType(typeKey: string, values: EligibilityCurrentValues): boolean {
    return getPropertyTypePresence(values, typeKey) === true;
}

export function isCurrentInstallationType(typeKey: string, values: EligibilityCurrentValues): boolean {
    return getInstallationPresence(values, typeKey) === true;
}

export function isCurrentNestedEligibilityRow(
    parentKey: string,
    nestedKey: string,
    values: EligibilityCurrentValues,
): boolean {
    if (parentKey === 'propertyType') return isCurrentPropertyType(nestedKey, values);
    if (parentKey === 'installationType') return isCurrentInstallationType(nestedKey, values);
    return false;
}

function applicableEligibilityValueKeys(): string[] {
    return [
        'state',
        'multiplier',
        'ownershipType',
        ...PROPERTY_TYPE_ROWS.map((row) => row.key),
        ...INSTALLATION_TYPE_ROWS.map((row) => row.key),
        ...SYSTEM_SPEC_ROWS.map((row) => row.key),
    ];
}

export type FinanceEligibilityScore = {
    points: number;
    successCount: number;
    warningCount: number;
    failedCount: number;
    totalCount: number;
};

export function scoreFinanceEligibilityPartner(
    statuses: Record<string, EligibilityStatus>,
    _values: EligibilityCurrentValues,
): FinanceEligibilityScore {
    const keys = applicableEligibilityValueKeys();
    let successCount = 0;
    let warningCount = 0;
    let failedCount = 0;
    for (const key of keys) {
        const status = statuses[key];
        if (status === 'success') successCount += 1;
        else if (status === 'failed') failedCount += 1;
        else warningCount += 1;
    }
    return {
        points: successCount + warningCount * 0.5,
        successCount,
        warningCount,
        failedCount,
        totalCount: keys.length,
    };
}

export function formatFinanceEligibilityScore(score: Pick<FinanceEligibilityScore, 'points' | 'totalCount'>): string {
    if (!score.totalCount) return '—';
    const points = Number.isInteger(score.points) ? String(score.points) : score.points.toFixed(1);
    return `${points}/${score.totalCount}`;
}

export function isBetterFinanceEligibilityScore(
    candidate: FinanceEligibilityScore,
    currentBest: FinanceEligibilityScore,
): boolean {
    if (candidate.points !== currentBest.points) return candidate.points > currentBest.points;
    if (candidate.failedCount !== currentBest.failedCount) return candidate.failedCount < currentBest.failedCount;
    return candidate.successCount > currentBest.successCount;
}

export function getSuggestedFinanceEligibilityPartner(
    catalog: FinanceEligibilityCatalog | null | undefined,
    values: EligibilityCurrentValues,
): FinanceEligibilityPartner | null {
    let best: { partner: FinanceEligibilityPartner; score: FinanceEligibilityScore } | null = null;
    for (const partner of catalog?.partners ?? []) {
        if (!isFinanceEligibilityValuePartner(partner)) continue;
        const score = scoreFinanceEligibilityPartner(
            evaluateFinanceEligibility(partner.displayName, values, catalog),
            values,
        );
        if (!best || isBetterFinanceEligibilityScore(score, best.score)) {
            best = { partner, score };
        }
    }
    return best?.partner ?? null;
}

function evaluateMappedRule(
    rules: Record<string, EligibilityRuleResult> | undefined,
    current: string,
    aliases: Record<string, string>,
): EligibilityStatus {
    if (!current?.trim()) return 'warning';
    const key = normalizeLookupKey(current, aliases);
    if (!key || !rules?.[key]) return 'warning';
    return toStatus(rules[key]);
}

function knownPropertyTypeKeys(values: EligibilityCurrentValues): string[] {
    const raw = String(values.propertyType ?? '').trim();
    if (!raw) return [];
    const tokens = [raw, ...raw.split(/[,;|\n]+/).map((part) => part.trim()).filter(Boolean)];
    const keys: string[] = [];
    const seen = new Set<string>();
    for (const token of tokens) {
        const key = normalizeLookupKey(token, PROPERTY_TYPE_ALIASES);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        keys.push(key);
    }
    return keys;
}

function getPropertyTypePresence(values: EligibilityCurrentValues, typeKey: string): boolean | null {
    const keys = knownPropertyTypeKeys(values);
    if (!keys.length) return null;
    return keys.includes(typeKey) ? true : null;
}

function evaluatePropertyTypeChild(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    typeKey: string,
): EligibilityStatus {
    const presence = getPropertyTypePresence(values, typeKey);
    if (presence == null) return 'warning';
    if (!presence) return 'success';
    return toStatus(partner.propertyType?.[typeKey]);
}

function evaluatePropertyType(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    const knownStatuses = PROPERTY_TYPE_ROWS.map((row) => {
        const presence = getPropertyTypePresence(values, row.key);
        if (presence == null) return null;
        return evaluatePropertyTypeChild(partner, values, row.key);
    }).filter((status): status is EligibilityStatus => status != null);
    return worstStatus(knownStatuses);
}

function isEnergyCommunityPreferredPartner(partner: Pick<FinanceEligibilityPartner, 'id' | 'displayName'>): boolean {
    return /enfin/i.test(`${partner.id} ${partner.displayName}`);
}

function evaluateEnergyCommunity(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    if (!values.isEnergyCommunity) return 'success';
    return isEnergyCommunityPreferredPartner(partner) ? 'success' : 'warning';
}

function evaluateState(partner: FinanceEligibilityPartner, values: EligibilityCurrentValues): EligibilityStatus {
    const state = values.stateCode.trim().toUpperCase();
    if (!state) return 'warning';
    if (partner.paused) return 'failed';
    const sellable = (partner.state?.currentlySellable ?? []).map((item) => item.toUpperCase());
    if (sellable.includes(state)) return 'success';
    return 'failed';
}

function evaluateMultiplier(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    if (partner.multiplier?.notRequired) return 'success';
    if (!Number.isFinite(values.multiplier)) return 'warning';
    const state = values.stateCode.trim().toUpperCase();
    const min = partner.multiplier?.byState?.[state];
    if (min == null || !Number.isFinite(min)) return 'warning';
    return values.multiplier + 1e-9 >= min ? 'success' : 'failed';
}

function getInstallationPresence(values: EligibilityCurrentValues, typeKey: string): boolean | null {
    if (values.installationPresence && Object.prototype.hasOwnProperty.call(values.installationPresence, typeKey)) {
        return values.installationPresence[typeKey];
    }
    if (values.installationKeys.includes(typeKey)) return true;
    return null;
}

function evaluateInstallationChild(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
    typeKey: string,
): EligibilityStatus {
    const presence = getInstallationPresence(values, typeKey);
    if (presence == null) return 'warning';
    if (!presence) return 'success';
    return toStatus(partner.installationType?.[typeKey]);
}

function evaluateInstallation(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    const knownStatuses = INSTALLATION_TYPE_ROWS.map((row) => {
        const presence = getInstallationPresence(values, row.key);
        if (presence == null) return null;
        return evaluateInstallationChild(partner, values, row.key);
    }).filter((status): status is EligibilityStatus => status != null);
    return worstStatus(knownStatuses);
}

function evaluateTilt(partner: FinanceEligibilityPartner, values: EligibilityCurrentValues): EligibilityStatus {
    const min = partner.systemSpecs?.tilt?.min;
    const max = partner.systemSpecs?.tilt?.max;
    if (min == null && max == null) return 'warning';
    if (values.tiltMin == null || values.tiltMax == null) return 'warning';
    if (min != null && values.tiltMin < min) return 'failed';
    if (max != null && values.tiltMax > max) return 'failed';
    return 'success';
}

function evaluateSystemSize(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    const rule = partner.systemSpecs?.systemSize;
    if (!rule) return 'warning';
    if (rule.notKwCapped) return 'warning';
    if (!Number.isFinite(values.totalSystemSize) || values.totalSystemSize <= 0) return 'warning';
    const state = values.stateCode.trim().toUpperCase();
    const maxKw = rule.maxKwByState?.[state] ?? rule.defaultMaxKw;
    if (rule.minKw != null && values.totalSystemSize < rule.minKw) return 'failed';
    if (maxKw != null && values.totalSystemSize > maxKw) return 'failed';
    return 'success';
}

function evaluatePresenceRule(
    rule: { whenPresent?: EligibilityRuleResult; whenAbsent?: EligibilityRuleResult } | undefined,
    present: boolean | null,
): EligibilityStatus {
    if (!rule) return 'warning';
    if (present == null) return 'warning';
    return toStatus(present ? rule.whenPresent : rule.whenAbsent);
}

function evaluateBattery(partner: FinanceEligibilityPartner, values: EligibilityCurrentValues): EligibilityStatus {
    const rule = partner.systemSpecs?.battery;
    if (!rule) return 'warning';
    if (values.hasBattery) {
        const state = values.stateCode.trim().toUpperCase();
        if ((rule.failInStates ?? []).map((item) => item.toUpperCase()).includes(state)) {
            return 'failed';
        }
    }
    return evaluatePresenceRule(rule, values.hasBattery);
}

function evaluateMinimumPanel(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    const rule = partner.systemSpecs?.minimumPanel;
    if (!rule?.minCount) return 'warning';
    const count = rule.scope === 'system' ? values.moduleCount : values.smallestArrayCount;
    if (count == null || !Number.isFinite(count) || count <= 0) return 'warning';
    return count >= rule.minCount ? 'success' : 'failed';
}

function parsePercent(value: string): number | null {
    const n = Number(String(value).replace('%', '').trim());
    return Number.isFinite(n) ? n : null;
}

function evaluateMaxProdUsage(
    partner: FinanceEligibilityPartner,
    values: EligibilityCurrentValues,
): EligibilityStatus {
    const rule = partner.systemSpecs?.maxProdUsage;
    if (!rule) return 'warning';
    const state = values.stateCode.trim().toUpperCase();
    const stateRule = (rule.byState?.[state] ?? {}) as {
        noCap?: boolean;
        utilityDiscretion?: boolean;
        passMaxPercent?: number;
        failAbovePercent?: number;
        warningMinPercent?: number;
        warningMaxPercent?: number;
        maxSystemSizeKw?: number;
    };
    const effective = { ...rule, ...stateRule };

    if (effective.maxSystemSizeKw != null) {
        if (!Number.isFinite(values.totalSystemSize) || values.totalSystemSize <= 0) return 'warning';
        return values.totalSystemSize <= effective.maxSystemSizeKw ? 'success' : 'failed';
    }
    if (effective.noCap) return 'success';

    const offset =
        parsePercent(values.utilityOffsetPercent) ?? parsePercent(values.actualOffsetPercent);
    if (offset == null) return 'warning';
    if (effective.failAbovePercent != null && offset > effective.failAbovePercent) return 'failed';
    if (
        effective.warningMinPercent != null &&
        effective.warningMaxPercent != null &&
        offset >= effective.warningMinPercent &&
        offset <= effective.warningMaxPercent
    ) {
        return 'warning';
    }
    if (effective.passMaxPercent != null && offset <= effective.passMaxPercent) return 'success';
    if (effective.utilityDiscretion) return 'warning';
    if (effective.passMaxPercent != null) return 'failed';
    return 'warning';
}
